import Head from 'next/head';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface HreflangTag {
  hreflang: string;
  href: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: any;
  noIndex?: boolean;
  lang?: string;
  breadcrumbs?: BreadcrumbItem[];
  hreflang?: HreflangTag[];
}

export default function SEOHead({
  title,
  description,
  keywords = 'gadgetfever, gadgetfever eu, dropshipping store europe, electronics online store europe, buy gadgets online, smartphones online, laptops online, home electronics, wearables, secure stripe checkout',
  canonicalUrl,
  ogImage = '/GadgetFever/og.webp',
  structuredData,
  noIndex = false,
  lang = 'en',
  breadcrumbs,
  hreflang,
}: SEOHeadProps) {
  const baseUrl = 'https://www.gadgetfever.eu';
  const normalizeSite = (value: string) => {
    const parts = value
      .split('|')
      .map((p) => p.trim())
      .filter(
        (p) =>
          p.length > 0 &&
          !/^gadgetfever$/i.test(p)
      );
    const base = parts[0] || 'GadgetFever';
    return `${base} | GadgetFever`;
  };
  const normalizedTitle = normalizeSite(title);
  const fullCanonicalUrl =
    typeof canonicalUrl === 'string' && canonicalUrl.length > 0
      ? canonicalUrl.startsWith('http://') || canonicalUrl.startsWith('https://')
        ? canonicalUrl
        : `${baseUrl}${canonicalUrl.startsWith('/') ? canonicalUrl : `/${canonicalUrl}`}`
      : undefined;

  const absoluteOgImage =
    typeof ogImage === 'string' && (ogImage.startsWith('http://') || ogImage.startsWith('https://'))
      ? ogImage
      : `${baseUrl}${ogImage && ogImage.startsWith('/') ? '' : '/'}${ogImage || 'GadgetFever/og.webp'}`;
  const isLithuanian = false;
  
  const safeDescription =
    description ||
    'GadgetFever.eu is a European dropshipping store for electronics, smartphones, laptops, home devices and wearables with secure checkout and fast shipping.';

  const effectiveHreflang = hreflang && hreflang.length > 0 ? hreflang : [];

  const siteNavigationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name: isLithuanian ? 'Navigacija' : 'Navigation',
    url: `${baseUrl}/`,
    hasPart: [
      {
        '@type': 'SiteNavigationElement',
        name: 'Home',
        url: `${baseUrl}/`,
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Shop',
        url: `${baseUrl}/shop`,
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Contact',
        url: `${baseUrl}/contact`,
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'FAQ',
        url: `${baseUrl}/sub/faq`,
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Privacy Policy',
        url: `${baseUrl}/sub/privacy-policy`,
      },
    ],
  };

  // Breadcrumb Schema - dynamic based on page
  const generateBreadcrumbSchema = (): any => {
    if (!breadcrumbs || breadcrumbs.length === 0) {
      // Default breadcrumb: Home > Current Page
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${baseUrl}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: normalizedTitle.split(' | ')[0],
            item: fullCanonicalUrl || `${baseUrl}/`,
          },
        ],
      };
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url.startsWith('http') ? crumb.url : `${baseUrl}${crumb.url}`,
      })),
    };
  };

  const breadcrumbSchema = generateBreadcrumbSchema();

  // Default structured data for web pages
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: normalizedTitle,
    description: safeDescription,
    url: fullCanonicalUrl || `${baseUrl}/`,
    inLanguage: isLithuanian ? 'lt' : 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: 'GadgetFever',
      url: baseUrl,
    },
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <>
      <Head>
        <title>{normalizedTitle}</title>
        <meta name='description' content={safeDescription} />
        <meta name='keywords' content={keywords} />
        <meta name='author' content='GadgetFever' />
        <meta
          name='robots'
          content={
            noIndex
              ? 'noindex, nofollow'
              : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
          }
        />
        <meta
          name='googlebot'
          content={noIndex ? 'noindex, nofollow' : 'index, follow'}
        />
        <meta
          name='bingbot'
          content={noIndex ? 'noindex, nofollow' : 'index, follow'}
        />

        {/* Open Graph - absolute URLs for Facebook/social previews */}
        <meta property='og:title' content={normalizedTitle} />
        <meta property='og:description' content={safeDescription} />
        <meta property='og:url' content={fullCanonicalUrl || `${baseUrl}/`} />
        <meta property='og:image' content={absoluteOgImage} />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta property='og:image:type' content={absoluteOgImage.endsWith('.jpg') || absoluteOgImage.endsWith('.jpeg') ? 'image/jpeg' : 'image/png'} />
        <meta property='og:type' content='website' />
        <meta property='og:site_name' content='GadgetFever' />
        <meta property='og:locale' content={isLithuanian ? 'lt_LT' : 'en_US'} />

        {/* Twitter Card */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:site' content='@gadgetfever' />
        <meta name='twitter:creator' content='@gadgetfever' />
        <meta name='twitter:title' content={normalizedTitle} />
        <meta name='twitter:description' content={safeDescription} />
        <meta name='twitter:image' content={absoluteOgImage} />

        {/* Additional SEO */}
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <meta name='theme-color' content='#1f2937' />
        <meta name='msapplication-TileColor' content='#1f2937' />
        {fullCanonicalUrl ? <link rel='canonical' href={fullCanonicalUrl} /> : null}

        {/* Language alternates - Auto-generated from hreflangMap when canonicalUrl provided */}
        {effectiveHreflang.length > 0 &&
          effectiveHreflang.map((tag) => (
            <link
              key={tag.hreflang}
              rel='alternate'
              hrefLang={tag.hreflang}
              href={tag.href}
            />
          ))}

        {/* Structured Data - WebPage */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(finalStructuredData),
          }}
        />
        
        {/* Structured Data - Site Navigation (for Sitelinks) */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(siteNavigationSchema),
          }}
        />
        
        {/* Structured Data - Breadcrumbs */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema),
          }}
        />
      </Head>
    </>
  );
}
